import { type FC, useMemo, useState } from 'react';

import {
  Avatar,
  Button,
  CircularProgress,
  Dialog,
  Icon,
  Paper,
  Typography,
} from '@/ui';
import {
  formatCadenceLine,
  normalizeMemberBookingSlots,
  timezoneAbbreviation,
} from '@/utils';

import type {
  CoachCadence,
  MemberBiweeklyCurrentSeries,
  MemberChemistryBookingSlot,
  RescheduleMemberBiweeklyResponse,
} from '@/types';

import { MentoCalPageShell } from '@/components/MentoCal/PageShell';
import {
  useGetMemberBiweeklyRescheduleOptionsQuery,
  useRescheduleMemberBiweeklyMutation,
} from '@/store/api/memberBiweeklyBookingApi';
import {
  clearRescheduleMutationId,
  getRescheduleMutationId,
  isNonRetryableClientError,
  rescheduleMutationFingerprint,
} from '@/utils/rescheduleMutation';

const getViewerTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const slotStartTime = (slot: MemberChemistryBookingSlot) =>
  slot.startTime.trim();

const formatDateLabel = (date: string) =>
  new Date(`${date}T00:00:00`).toLocaleDateString('en', {
    day: 'numeric',
    month: 'short',
    weekday: 'short',
  });

const formatConfirmedSeries = (firstStart: string, timezone: string) => {
  const start = new Date(firstStart);
  const day = start.toLocaleDateString('en', {
    timeZone: timezone,
    weekday: 'long',
  });
  const time = start.toLocaleTimeString('en', {
    hour: 'numeric',
    hour12: true,
    minute: '2-digit',
    timeZone: timezone,
  });
  const tz = timezoneAbbreviation(timezone);
  return `Every other ${day} at ${time}${tz ? ` ${tz}` : ''}`;
};

const formatNextSession = (firstStart: string, timezone: string) => {
  const start = new Date(firstStart);
  const date = start.toLocaleDateString('en', {
    day: 'numeric',
    month: 'short',
    timeZone: timezone,
    weekday: 'long',
  });
  return `Next session ${date}`;
};

const formatCurrentSeries = (currentSeries: MemberBiweeklyCurrentSeries) => {
  const tz = timezoneAbbreviation(currentSeries.timezone);
  return `Every other ${currentSeries.weekday} at ${currentSeries.time}${tz ? ` ${tz}` : ''}`;
};

const currentSeriesLabel = (
  currentSeries: MemberBiweeklyCurrentSeries | undefined,
  cadence: CoachCadence | undefined,
) => {
  if (currentSeries) return formatCurrentSeries(currentSeries);
  if (cadence) return formatCadenceLine(cadence);
  return 'Loading schedule...';
};

interface Props {
  cadence?: CoachCadence;
  coach: {
    avatarUrl?: null | string;
    firstName?: string;
    fullName?: string;
    id: string;
    lastName?: string;
  };
  memberEmail?: string;
  onClose: () => void;
  onDone: () => void;
}

const MemberBiweeklyReschedule: FC<Props> = ({
  cadence,
  coach,
  memberEmail,
  onClose,
  onDone,
}) => {
  const viewerTimezone = useMemo(getViewerTimezone, []);
  const [selectedStartTime, setSelectedStartTime] = useState('');
  const [confirmedSeries, setConfirmedSeries] = useState<
    RescheduleMemberBiweeklyResponse['series'] | null
  >(null);
  const [error, setError] = useState('');
  const { data, isError, isFetching, isLoading } =
    useGetMemberBiweeklyRescheduleOptionsQuery({
      coachId: coach.id,
      viewerTimezone,
    });
  const [reschedule, rescheduleResult] = useRescheduleMemberBiweeklyMutation();
  const availabilitySlots = (data?.availability.days ?? []).flatMap((day) =>
    normalizeMemberBookingSlots(day.slots).map((slot) => ({
      date: day.date,
      enabled: day.enabled,
      slot,
    })),
  );
  const currentSeries = data?.currentSeries ?? cadence;
  const timezone = data?.timezone ?? currentSeries?.timezone ?? viewerTimezone;
  const currentSeriesText = currentSeriesLabel(data?.currentSeries, cadence);

  let optionsContent = null;
  if (isLoading) {
    optionsContent = (
      <div className="flex items-center gap-2">
        <CircularProgress size={18} />
        <Typography>Loading options...</Typography>
      </div>
    );
  } else if (isError) {
    optionsContent = (
      <Typography className="text-red-600">
        We couldn't load available times. Please try again.
      </Typography>
    );
  } else if (availabilitySlots.length === 0) {
    optionsContent = <Typography>No available times found.</Typography>;
  } else {
    optionsContent = (
      <div className="flex flex-col gap-2">
        {availabilitySlots.map(({ date, enabled, slot }) => {
          const startTime = slotStartTime(slot);
          const selected = selectedStartTime === startTime;
          return (
            <Button
              key={`${date}-${startTime}`}
              disabled={!enabled}
              variant={selected ? 'contained' : 'outlined'}
              onClick={() => setSelectedStartTime(startTime)}
            >
              {formatDateLabel(date)} at {slot.label}
            </Button>
          );
        })}
      </div>
    );
  }

  const onConfirm = async () => {
    if (!selectedStartTime) return;
    if (!data?.currentSeries.seriesId) {
      setError('Current series is still loading. Please try again.');
      return;
    }

    const fingerprint = rescheduleMutationFingerprint([
      'member',
      coach.id,
      data.currentSeries.seriesId,
      'future_series',
      data.currentSeries.nextStart,
      selectedStartTime,
    ]);
    const mutationId = getRescheduleMutationId(fingerprint);
    try {
      setError('');
      const response = await reschedule({
        coachId: coach.id,
        mutationId,
        seriesId: data.currentSeries.seriesId,
        startTime: selectedStartTime,
        viewerTimezone,
      }).unwrap();
      setConfirmedSeries(response.series);
      clearRescheduleMutationId(fingerprint);
    } catch (caughtError) {
      if (isNonRetryableClientError(caughtError)) {
        clearRescheduleMutationId(fingerprint);
      }
      setError('That time is no longer available. Please choose another time.');
    }
  };

  if (confirmedSeries) {
    const coachName =
      coach.fullName ||
      `${coach.firstName ?? ''} ${coach.lastName ?? ''}`.trim();
    const [nameFirst = '', ...nameLastParts] = coachName.split(' ');
    const coachFirstName = coach.firstName || nameFirst;
    const coachLastName = coach.lastName || nameLastParts.join(' ');
    return (
      <MentoCalPageShell
        labelledBy="member-biweekly-update-confirmation-title"
        onClose={onDone}
      >
        <Paper
          className="flex w-full max-w-[482px] flex-col gap-4 p-8"
          elevation={4}
        >
          <div className="flex items-center gap-3">
            <Avatar
              firstName={coachFirstName}
              lastName={coachLastName}
              size="s"
              src={coach.avatarUrl ?? ''}
            />
            <Typography className="text-sm font-medium">{coachName}</Typography>
          </div>
          <Typography
            className="font-medium"
            id="member-biweekly-update-confirmation-title"
            variant="h5"
          >
            Biweekly session updated!
          </Typography>
          {memberEmail ? (
            <div className="flex items-start gap-3 text-cement-400">
              <Icon className="text-xl">email</Icon>
              <Typography>Updated in {memberEmail} calendar</Typography>
            </div>
          ) : null}
          <div className="flex items-start gap-3 text-cement-400">
            <Icon className="text-xl">event</Icon>
            <div>
              <Typography>
                {formatConfirmedSeries(confirmedSeries.firstStart, timezone)}
              </Typography>
              <Typography className="text-sm">
                {formatNextSession(confirmedSeries.firstStart, timezone)}
              </Typography>
            </div>
          </div>
          <div className="flex items-start gap-3 text-cement-400">
            <Icon className="text-xl">event</Icon>
            <div>
              <Typography className="text-sm">Former time</Typography>
              <Typography className="line-through">
                {currentSeriesText}
              </Typography>
            </div>
          </div>
          <div className="flex items-start gap-3 text-cement-400">
            <Icon className="text-xl">info_outlined</Icon>
            <Typography className="text-sm">
              You can reschedule any session up to 48 hours before it starts,
              from your calendar invite or in Mento.
            </Typography>
          </div>
          <div className="flex justify-end">
            <Button variant="contained" onClick={onDone}>
              Done
            </Button>
          </div>
        </Paper>
      </MentoCalPageShell>
    );
  }

  return (
    <Dialog
      aria-labelledby="member-biweekly-update-title"
      open
      onClose={onClose}
    >
      <Paper className="max-w-xl p-6" elevation={4}>
        <Typography
          className="mb-2"
          id="member-biweekly-update-title"
          variant="h5"
        >
          Update Day and Time
        </Typography>
        <Typography className="mb-4 text-cement-400">
          Current series: {currentSeriesText}
        </Typography>

        {optionsContent}

        {error && (
          <Typography className="mt-3 text-red-600">{error}</Typography>
        )}
        <div className="mt-4 flex gap-2">
          <Button variant="outlined" onClick={onClose}>
            Back
          </Button>
          <Button
            disabled={
              !selectedStartTime || isFetching || rescheduleResult.isLoading
            }
            onClick={onConfirm}
          >
            {rescheduleResult.isLoading ? 'Updating...' : 'Confirm'}
          </Button>
        </div>
      </Paper>
    </Dialog>
  );
};

export default MemberBiweeklyReschedule;
